import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { useState } from "react";
import { api } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { formatNumber, cn } from "@/lib/utils";
import { ArrowRight } from "lucide-react";

const platforms = ["all", "YT", "TT", "IG", "FB"] as const;

export default function Filmy() {
  const { data } = useQuery({ queryKey: ["videos"], queryFn: api.listVideos });
  const [filter, setFilter] = useState<(typeof platforms)[number]>("all");

  const rows = (data ?? []).filter((v) => filter === "all" || v.platform === filter);

  return (
    <div>
      <PageHeader title="Filmy" subtitle="Rolki z YouTube, TikTok, Instagram, Facebook" />

      <div className="mb-4 flex gap-2">
        {platforms.map((p) => (
          <Button
            key={p}
            size="sm"
            variant={filter === p ? "default" : "outline"}
            onClick={() => setFilter(p)}
          >
            {p === "all" ? "Wszystkie" : p}
          </Button>
        ))}
      </div>

      <Card>
        <CardContent className="p-0">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border/50 text-xs uppercase tracking-wider text-muted-foreground">
                <th className="px-5 py-3 text-left font-medium">Data</th>
                <th className="px-5 py-3 text-left font-medium">Platforma</th>
                <th className="px-5 py-3 text-left font-medium">Tytuł</th>
                <th className="px-5 py-3 text-right font-medium">Wyświetlenia</th>
                <th className="px-5 py-3 text-right font-medium">CTR</th>
                <th className="px-5 py-3 text-right font-medium">Komentarze</th>
                <th className="px-5 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((v, i) => (
                <motion.tr
                  key={i}
                  initial={{ opacity: 0, y: 4 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.03 }}
                  className="border-b border-border/30 last:border-0 hover:bg-muted/30"
                >
                  <td className="px-5 py-3 text-sm tabular-nums">{v.date}</td>
                  <td className="px-5 py-3">
                    <PlatformBadge platform={v.platform} />
                  </td>
                  <td className="px-5 py-3 text-sm">{v.title}</td>
                  <td className="px-5 py-3 text-right font-mono text-sm tabular-nums">
                    {formatNumber(v.views)}
                  </td>
                  <td className="px-5 py-3 text-right font-mono text-sm text-muted-foreground tabular-nums">
                    {v.ctr != null ? `${Math.round(v.ctr * 100)}%` : "—"}
                  </td>
                  <td className="px-5 py-3 text-right font-mono text-sm text-muted-foreground tabular-nums">
                    {v.comments_count}
                  </td>
                  <td className="px-5 py-3">
                    <Link
                      to={`/komentarze?video=${encodeURIComponent(v.title)}`}
                      className="text-xs text-brand hover:underline flex items-center gap-1"
                    >
                      komentarze <ArrowRight className="h-3 w-3" />
                    </Link>
                  </td>
                </motion.tr>
              ))}
              {rows.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-5 py-8 text-center text-sm text-muted-foreground">
                    Brak filmów dla wybranego filtra.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}

function PlatformBadge({ platform }: { platform: string }) {
  const colors: Record<string, string> = {
    YT: "bg-red-500/20 text-red-400",
    TT: "bg-pink-500/20 text-pink-400",
    IG: "bg-purple-500/20 text-purple-400",
    FB: "bg-blue-500/20 text-blue-400",
  };
  return (
    <div
      className={cn(
        "inline-flex h-6 items-center justify-center rounded-md px-2 text-[11px] font-bold",
        colors[platform] ?? "bg-muted text-muted-foreground",
      )}
    >
      {platform}
    </div>
  );
}
